import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PreviewHeader from "./Common/PreviewHeader";
import CategoryTabs from "./Common/CategoryTabs";
import ProductPreview from "./Templates/ProductPreview";
import Spinners from "components/common/Spinners";
import ProductGrid from "./Templates/ProductGrid";
import ProductList from "./Templates/ProductList";
import HarvestGrid from "./Templates/HarvestGrid";
import noteIcon from "assets/icons/note.svg";
import TextMenuGrid from "./Templates/TextMenuGrid";

const API_URL = process.env.REACT_APP_API_URL;
const PREVIEW_AUTH_KEY = process.env.REACT_APP_PREVIEW_AUTH_KEY;

const buildPreviewStorageKey = (idOrToken, lang) => {
  const identifier = encodeURIComponent(idOrToken || "unknown");
  return `ease_preview_data_${identifier}_${lang}`;
};

const loadPreviewCache = (idOrToken, lang) => {
  if (!idOrToken) return null;
  try {
    const raw = localStorage.getItem(buildPreviewStorageKey(idOrToken, lang));
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.warn("Failed to read preview cache", error);
    return null;
  }
};

const savePreviewCache = (idOrToken, lang, data) => {
  if (!idOrToken || !data) return;
  try {
    localStorage.setItem(
      buildPreviewStorageKey(idOrToken, lang),
      JSON.stringify(data)
    );
  } catch (error) {
    console.warn("Failed to save preview cache", error);
  }
};

const Preview = ({ mode }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const token = decodeURIComponent(params.get("token") || "");
  const toggleLanguage = params.get("toggleLanguage") || "en";

  const { menuId } = useParams();
  const idOrToken = mode === "menuId" && menuId ? menuId : token;
  const cachedData = loadPreviewCache(idOrToken, toggleLanguage);

  const [previewData, setPreviewData] = useState(cachedData || {});
  const [loading, setLoading] = useState(cachedData ? false : true);
  const [language, setLanguage] = useState(toggleLanguage);
  const [activeCategoryId, setActiveCategoryId] = useState(
    cachedData?.body?.categoryTab?.lists?.[0]?.categoryId || null
  );
  const [activeCategoryName, setActiveCategoryName] = useState(null);
  const [isAPIResponseSuccess, setIsAPIResponseSuccess] = useState(
    cachedData ? true : null
  );
  const [apiResponseMsg, setApiResponseMsg] = useState(
    cachedData ? "Loaded preview from cache" : null
  );

  const hydratePreviewData = (data) => {
    setPreviewData(data);
    const firstCategory = data?.body?.categoryTab?.lists?.[0];
    if (firstCategory) {
      setActiveCategoryId(firstCategory.categoryId);
    }
  };

  useEffect(() => {
    setLanguage(toggleLanguage);
    if (idOrToken) {
      fetchPreviewData(idOrToken, toggleLanguage, {
        showLoading: !cachedData,
      });
    } else {
      setLoading(false);
      setIsAPIResponseSuccess(false);
      setApiResponseMsg("No preview found");
    }
  }, [location.search, menuId]);

  useEffect(() => {
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = language;
  }, [language]);

  const fetchPreviewData = async (
    token,
    lang = "en",
    options = { showLoading: true }
  ) => {
    try {
      if (options.showLoading) {
        setLoading(true);
      }

      let url;

      if (mode === "menuId" && menuId) {
        url = `${API_URL}/web/restaurant/menu/${menuId}?toggleLanguage=${lang}`;
      } else {
        url = `${API_URL}/web/layout/preview/${token}?toggleLanguage=${lang}`;
      }

      const response = await axios.get(url, {
        headers: {
          Authorization: PREVIEW_AUTH_KEY,
        },
      });

      const { success, data, message } = response?.data || {};
      setIsAPIResponseSuccess(success);
      setApiResponseMsg(message);
      if (success) {
        hydratePreviewData(data);
        savePreviewCache(token, lang, data);
      }
    } catch (error) {
      console.error(
        "Failed to fetch preview data:",
        error?.response?.data || error.message
      );
      if (!previewData?.body) {
        setIsAPIResponseSuccess(false);
        setApiResponseMsg(
          error?.response?.data?.message || error.message || "Preview fetch failed"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    const params = new URLSearchParams(location.search);

    // Update toggleLanguage param
    params.set("toggleLanguage", lang);
    navigate(`?${params.toString()}`, { replace: true }); // Update URL

    // Refetch preview data based on mode
    if (mode === "menuId" && menuId) {
      fetchPreviewData(menuId, lang);
    } else {
      const token = params.get("token");
      if (token) {
        fetchPreviewData(token, lang);
      }
    }
  };

  const primaryColor = previewData?.theme?.fontStyle?.primaryColor || "#A47E51";
  const currencyInfo = previewData?.currencyInfo || {};

  const { layoutType, scrollDirection, theme, header, body } =
    previewData || {};

  const products = body?.content?.lists || [];

  const { responsiveLayoutType } = header?.qrCode || {};
  const filteredProducts = useMemo(() => {
    return products.filter(
      (product) => product.categoryId === activeCategoryId
    );
  }, [products, activeCategoryId]);

  // Styles
  const themeBgImageStyle = useMemo(
    () => ({
      backgroundImage: theme?.backgroundImage?.enabled
        ? `url('${theme.backgroundImage.url}')`
        : "none",
      opacity: theme?.backgroundImage?.opacity || 1,
    }),
    [theme]
  );

  const themeBgColorStyle = useMemo(
    () => ({
      backgroundColor: theme?.backgroundColor?.enabled
        ? theme.backgroundColor.color
        : "transparent",
      opacity: theme?.backgroundColor?.opacity || 1,
    }),
    [theme]
  );

  const bgImage = body?.styles?.backgroundImage;
  const bgColor = body?.styles?.backgroundColor;

  const bodyBackgroundStyle = useMemo(() => {
    if (bgImage?.enabled && bgImage?.url) {
      return {
        backgroundImage: `url('${bgImage.url}')`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
      };
    }
    return {};
  }, [bgImage]);

  const bgOverlayStyle = useMemo(() => {
    if (bgColor?.enabled && bgColor?.color) {
      return {
        backgroundColor: bgColor.color,
        opacity: bgColor.opacity ?? 1,
      };
    }
    return null;
  }, [bgColor]);

  const categoryTabEnabled = body?.categoryTab?.enabled;
  const isApplyBlackColor = ["productGalleryGrid"]?.includes(layoutType);
  const allCategories = body?.categoryTab?.lists ?? [];

  useEffect(() => {
    if (!activeCategoryId || !allCategories.length) return;

    const category = allCategories.find(
      (category) => category?.categoryId === activeCategoryId
    );

    if (category) {
      setActiveCategoryName({ ...category });
    }
  }, [activeCategoryId, allCategories]);

  console.log("responsiveLayoutType is ", responsiveLayoutType)

  return (
    <div className="public_preview_pages">
      {loading ? (
        <Spinners />
      ) : isAPIResponseSuccess ? (
        <div className={`theme_inner ${language === "en" ? "ltr" : "rtl"}`}>
          <div className="theme-bg-image" style={themeBgImageStyle} />
          <div className="theme-bg-color" style={themeBgColorStyle} />
          <PreviewHeader
            header={header}
            body={body}
            primaryColor={primaryColor}
            language={language}
            handleLanguageChange={handleLanguageChange}
            theme={theme}
          />
          <div className="preview_body" style={bodyBackgroundStyle}>
            {bgOverlayStyle && (
              <div className="bg_overlay" style={bgOverlayStyle} />
            )}
            <div className="preview_content">
              {/* CATEGORY TAB */}
              {categoryTabEnabled && (
                <CategoryTabs
                  lists={body.categoryTab.lists}
                  styles={body.categoryTab.styles}
                  activeCategoryId={activeCategoryId}
                  setActiveCategoryId={setActiveCategoryId}
                />
              )}
              {/* CONTENT */}
              <div className="content">
                {responsiveLayoutType === "productGrid2" && (
                  <ProductPreview
                    products={filteredProducts}
                    config={body.content.configSettings}
                    layoutSettings={body.content.layoutSettings}
                    styles={body.styles}
                    primaryColor={primaryColor}
                    theme={theme}
                    currencyInfo={currencyInfo}
                    language={language}
                    layoutType={layoutType}
                    isApplyBlackColor={isApplyBlackColor}
                    responsiveLayoutType={responsiveLayoutType}
                  />
                )}
                {(responsiveLayoutType === "productPreview" ||
                  responsiveLayoutType === "productGrid1" ||
                  responsiveLayoutType === "productGrid3") && (
                  <ProductGrid
                    products={filteredProducts}
                    config={body.content.configSettings}
                    layoutSettings={body.content.layoutSettings}
                    styles={body.styles}
                    primaryColor={primaryColor}
                    theme={theme}
                    currencyInfo={currencyInfo}
                    scrollDirection={scrollDirection}
                    language={language}
                    responsiveLayoutType={responsiveLayoutType}
                    isApplyBlackColor={isApplyBlackColor}
                  />
                )}
                {layoutType === "productList" && (
                  <ProductList
                    products={filteredProducts}
                    config={body.content.configSettings}
                    layoutSettings={body.content.layoutSettings}
                    styles={body.styles}
                    primaryColor={primaryColor}
                    theme={theme}
                    language={language}
                    currencyInfo={currencyInfo}
                  />
                )}
                {responsiveLayoutType === "harvestHarvest" && (
                  <HarvestGrid
                    products={filteredProducts}
                    config={body.content.configSettings}
                    layoutSettings={body.content.layoutSettings}
                    styles={body.styles}
                    primaryColor={primaryColor}
                    theme={theme}
                    language={language}
                    currencyInfo={currencyInfo}
                  />
                )}
                {responsiveLayoutType === "textMenuGrid" && (
                  <TextMenuGrid
                    products={filteredProducts}
                    config={body.content.configSettings}
                    layoutSettings={body.content.layoutSettings}
                    styles={body.styles}
                    primaryColor={primaryColor}
                    theme={theme}
                    currencyInfo={currencyInfo}
                    scrollDirection={scrollDirection}
                    language={language}
                    responsiveLayoutType={responsiveLayoutType}
                    isApplyBlackColor={isApplyBlackColor}
                    activeCategoryName={activeCategoryName}
                  />
                )}
              </div>
              <div className="owner_title">
                <a
                  style={{ color: theme?.fontStyle?.primaryColor }}
                  href="https://easesignage.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="powered_by"
                >
                  {language === "en"
                    ? "Powered by easesignage.com"
                    : "مشغل بواسطة easesignage.com"}
                </a>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="failed_preview_screen">
          <div>
            <img
              src={noteIcon}
              alt="note"
              width={40}
              height={40}
              className="d-block mx-auto mb-2"
            />
            <p className="text-center text_primary fs_18">{apiResponseMsg}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Preview;
